import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

// Prefix to prevent Perl backend from converting numeric strings to numbers
const FACILITY_CODE_PREFIX = 'FC:'

/**
 * Encode facility code with prefix to prevent backend number conversion
 */
export function encodeFacilityCode(code: string): string {
  if (!code) return ''
  return `${FACILITY_CODE_PREFIX}${code}`
}

/**
 * Decode facility code by stripping prefix
 */
export function decodeFacilityCode(value: string | number | undefined): string {
  if (!value) return ''
  const strValue = String(value)
  if (strValue.startsWith(FACILITY_CODE_PREFIX)) {
    return strValue.slice(FACILITY_CODE_PREFIX.length)
  }
  return strValue
}

export interface EvisitorCredentials {
  username: string
  password: string
}

export interface ConnectEvisitorPayload {
  credentials: EvisitorCredentials
  configuration?: Record<string, unknown>
}

export interface ConnectEvisitorResponse {
  response: {
    status: string
  }
  http_code: string
}

export interface ConnectEvisitorError {
  status_line: string
  error: string
}

export interface EvisitorConnection {
  pkey: string
  created: number
  name?: string
  service?: string
  status?: number
  default?: number
  owner_id?: string
  creator?: string
  handler?: string
  configuration?: Record<string, unknown>
}

export interface ComplianceRental {
  id: string
  name: string
  address: string
  city?: string
  state?: string
  country?: string
  status?: string
  image?: string
  evisitorFacilityId?: string
  evisitorGatewayId?: string
}

export interface EvisitorFacility {
  id: string
  code: string
  name: string
  address: string
  city?: string
  country?: string
  status: 'Active' | 'Pending' | 'Inactive'
}

export interface SuggestedMapping {
  localId: string
  evisitorId: string
  confidence: number
  note: string
}

export interface UpdateRentalEvisitorPayload {
  evisitor_facility_id?: string
  evisitor_facility_code?: string
  evisitor_gateway_id?: string
}

interface EvisitorFacilityApi {
  Code?: string
  StreetNumber?: string
  FacilityType?: string
  FlatRateOptionVisible?: string | null
  ID: string
  Note?: string | null
  Decision?: string | null
  Name: string
  Decision2?: string | null
  FacilityLinkToNaturalPerson?: boolean | null
  SettlementZone?: string | null
  DecisionUploadTime?: string | null
  Settlement?: string | null
  PaysTTFlatAmount?: string | null
  IsDeactivated?: boolean
  SettlementHrID?: string | null
  TTPayerID?: string | null
  TTPayerNaturalPerson?: boolean
  OpenFacilityPeriod?: string | null
  AccommodationUnits?: string | null
  Category?: string | null
}

interface EvisitorFacilitiesResponse {
  http_code: string
  response: {
    item?: {
      source?: string
      items?: EvisitorFacilityApi[]
      hash?: string
      name?: string
      fetchedAt?: number
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class ComplianceService {
  private readonly evisitorEndpoint = '@api/v1/providers/evisitor'

  constructor(private http: HttpClient) {}

  /**
   * Connect eVisitor provider account
   * Creates a global provider connection that can be used across properties
   */
  connectEvisitorProvider(payload: ConnectEvisitorPayload): Observable<ConnectEvisitorResponse> {
    const url = this.evisitorEndpoint

    return this.http.post<ConnectEvisitorResponse>(url, payload)
  }

  /**
   * Fetch all connected eVisitor providers for the current account
   */
  getEvisitorConnections(): Observable<EvisitorConnection[]> {
    return this.http.get<EvisitorConnection[]>(this.evisitorEndpoint)
  }

  /**
   * Fetch facilities for a specific eVisitor connection
   */
  getEvisitorFacilities(connectionPkey: string): Observable<EvisitorFacility[]> {
    const url = `@api/v1/providers/evisitor/${connectionPkey}/facilities`

    return this.http.get<EvisitorFacilitiesResponse>(url).pipe(
      map((response) => {
        const facilities = response?.response?.item?.items || []

        return facilities.map((facility) => this.toEvisitorFacility(facility))
      })
    )
  }

  /**
   * Update eVisitor configuration for a specific rental
   */
  updateRentalEvisitorConfig(rentalPkey: string, payload: UpdateRentalEvisitorPayload): Observable<unknown> {
    const url = `@api/rental/${rentalPkey}/evisitor`

    return this.http.put(url, payload)
  }

  private toEvisitorFacility(item: EvisitorFacilityApi): EvisitorFacility {
    return {
      id: item.ID,
      code: item.Code || '',
      name: item.Name?.trim() || item.ID,
      address: item.StreetNumber || '',
      city: item.Settlement || undefined,
      status: item.IsDeactivated ? 'Inactive' : 'Active',
    }
  }

  /**
   * Delete a specific eVisitor connection by pkey
   */
  deleteEvisitorConnection(pkey: string): Observable<EvisitorConnection> {
    return this.http.delete<EvisitorConnection>(`${this.evisitorEndpoint}/${pkey}`)
  }

  /**
   * Update eVisitor guest profile for a specific guest pkey
   */
  updateEvisitorGuest(pkey: string, payload: EVisitorGuestPayload): Observable<unknown> {
    return this.http.put(`@api/v1/providers/evisitor/guests/${pkey}`, payload)
  }

  /**
   * Add a guest to an inquiry's evisitor guest list
   */
  addGuestToInquiry(inquiryPkey: string, guestPkey: string): Observable<unknown> {
    return this.http.post(`@api/v1/providers/evisitor/inquiries/${inquiryPkey}/guests/${guestPkey}`, {})
  }

  /**
   * Remove a guest from an inquiry's evisitor guest list
   */
  removeGuestFromInquiry(inquiryPkey: string, guestPkey: string): Observable<unknown> {
    return this.http.delete(`@api/v1/providers/evisitor/inquiries/${inquiryPkey}/guests/${guestPkey}`)
  }

  /**
   * Fetch arrival organisation codelist
   */
  getArrivalOrganisations(gatewayId: string): Observable<EVisitorArrivalOrganisation[]> {
    return this.getCodelist<EVisitorArrivalOrganisation>('ArrivalOrganisationLookup', gatewayId)
  }

  /**
   * Fetch border crossing codelist
   */
  getBorderCrossings(gatewayId: string): Observable<EVisitorBorderCrossing[]> {
    return this.getCodelist<EVisitorBorderCrossing>('BorderCrossingHRlookup', gatewayId)
  }

  /**
   * Fetch payment category codelist (requires facility_id)
   */
  getPaymentCategories(gatewayId: string, facilityId: string): Observable<EVisitorPaymentCategory[]> {
    return this.getCodelist<EVisitorPaymentCategory>('TTPaymentCategoryLookup2', gatewayId, facilityId)
  }

  /**
   * Fetch accommodation unit type codelist
   */
  getAccommodationUnitTypes(gatewayId: string): Observable<EVisitorAccommodationUnitType[]> {
    return this.getCodelist<EVisitorAccommodationUnitType>('AccommodationUnitFacilityType', gatewayId)
  }

  /**
   * Fetch offered service type codelist
   */
  getOfferedServiceTypes(gatewayId: string): Observable<EVisitorOfferedServiceType[]> {
    return this.getCodelist<EVisitorOfferedServiceType>('OfferedServiceTypeLookup', gatewayId)
  }

  /**
   * Fetch country codelist
   */
  getCountries(gatewayId: string): Observable<EVisitorCountry[]> {
    return this.getCodelist<EVisitorCountry>('CountryLookup', gatewayId)
  }

  /**
   * Fetch document type codelist
   */
  getDocumentTypes(gatewayId: string): Observable<EVisitorDocumentType[]> {
    return this.getCodelist<EVisitorDocumentType>('DocumentTtypeLookup', gatewayId)
  }

  /**
   * Generic codelist fetcher
   */
  private getCodelist<T>(name: string, gatewayId: string, facilityId?: string): Observable<T[]> {
    let url = `${this.evisitorEndpoint}/codelists/${name}?gateway_id=${gatewayId}`
    if (facilityId) {
      url += `&facility_id=${facilityId}`
    }

    return this.http
      .get<EVisitorCodelistResponse<T>>(url)
      .pipe(map((response) => response?.response?.item?.items || []))
  }
}

export interface EVisitorGuestPayload {
  firstName: string
  middleName?: string
  lastName: string
  email?: string
  phone?: string
  gender: string
  citizenshipCountryCode: string
  dateOfBirth: number
  birthCountryCode: string
  cityOfBirth?: string
  residenceCountryCode: string
  cityOfResidence: string
  documentType: string
  documentId: string
}

// Codelist interfaces
export interface EVisitorArrivalOrganisation {
  ID: string
  Active: boolean
  Name: string
  CodeMI: string
  Sequence: number
}

export interface EVisitorBorderCrossing {
  ID: string
  Active: boolean
  CodeMI: string
  Name: string
  BorderCrossingType: string
}

export interface EVisitorPaymentCategory {
  ID: string
  Active: boolean
  FacilityID: string
  CodeNames: string
  Code: string
  IsVacationHomeCalculation: boolean
}

export interface EVisitorAccommodationUnitType {
  ID: string
  FacilityID: string
  NameCode: string
  FacilityCode: string
}

export interface EVisitorOfferedServiceType {
  ID: string
  Active: boolean
  Name: string
}

export interface EVisitorCountry {
  ID: string
  Active: boolean
  NameCitizenships: string
  CodeTwoLetters: string
  CodeNumber: string
  CodeThreeLetters: string
  NameNational: string
  NameNationalShort: string
  AlternativeName: string
  NameCitizenshipsShort: string
  IsEUMember: boolean
  IsVisaRequired: boolean
  IsEEAMember: boolean
  NameNationalAlternative: string
}

export interface EVisitorDocumentType {
  ID: string
  Active: boolean
  Code: string
  Name: string
  Sequence: number
  CodeMI: string
}

interface EVisitorCodelistResponse<T> {
  http_code: string
  response: {
    item?: {
      source?: string
      items?: T[]
      hash?: string
      name?: string
      fetchedAt?: number
    }
  }
}
